// @ts-strict-ignore
import { getSubsectorForTransitionElement } from '../../../../functions/transition-targets/get-subsector-for-transition-element';
import { sumArray } from '../../../../functions/utilities/array-utility';
import { TransitionElement } from '../../../../model/enums/transition-element';
import { TransitionTargetSubsector } from '../../../../model/enums/transition-target-subsector';
import { TargetWeight } from '../../../../model/target-balancing/target-weights';
import { CCCModelUtility } from '../../ccc-model-utility/ccc-model-utility';
import { EconomicStockImpactType } from '../../model/climate-cortex-model';
import { EvaluatedFactor } from '../../model/factor';
import { IGlobalExternality } from '../../model/global-externality';
import {
  EvaluatedMechanism,
  ITransitionElement,
  ResourceShiftElement,
  ShiftElement,
  SupplyUpdateElement,
  TransitionElementProps,
  TransitionElementType,
  UpdateCarbonCausalChainOptions,
  UpdateElement,
  UpdateFactorOptions,
  UpdateResourceOptions,
  UpdateType,
  UpshiftUpdateProps,
} from '../../model/transition-element';
import { getValueFromFactor, instantiateFactor } from '../../utils/factor-util';
import { CarbonCausalChain } from '../carbon-causal-chain/carbon-causal-chain';
import { KeyMap } from '../key-map';
import { Resource } from '../resource/resource';
import { Stock } from '../stock/stock';
import {
  SupplyAlterationData,
  SupplyTransitionData,
  SupplyUpdateData,
  TransitionData,
  TransitionElementCosts,
  TransitionResourceShiftData,
  TransitionShiftData,
  TransitionUpdateData,
  TransitionUpdateDirection,
  TransitionUpshiftData,
} from '../transition-data';
import { Variable } from '../variable/variable';

export class TransitionTarget {
  private _activityShift: number = 0;
  private _alternative: EvaluatedMechanism;
  private _cohort: EvaluatedFactor;
  private _current: EvaluatedMechanism;
  private _economicStockImpact: EconomicStockImpactType;
  private _id: TransitionElement;
  private _primaryUpdateParameterKey: string;
  private _resourceOptions: UpdateResourceOptions;
  private _subsector: TransitionTargetSubsector;
  private _supplyUpdateVariable: string;
  private _transitionType: TransitionElementType;
  private _updateOptions: UpdateFactorOptions[];
  private _weightInversion: EvaluatedFactor;

  private readonly _MAX_RESOURCE_PROPORTION_CAP: number = 100;

  get lhsCcc(): string[] {
    return this._current.cccIds;
  }
  get producedResources(): string[] {
    if (this._transitionType !== TransitionElementType.SupplyAlteration) {
      return undefined;
    }
    return this._current.cccs.map((ccc) => ccc.producedResources).flat();
  }
  get rhsCcc(): string[] {
    return this._alternative.cccIds || [];
  }
  get subsector(): TransitionTargetSubsector {
    return this._subsector;
  }
  get supplyUpdateVariable(): string {
    return this._supplyUpdateVariable;
  }
  get transitionType(): TransitionElementType {
    return this._transitionType;
  }
  get updateOptions(): UpdateFactorOptions[] {
    return this._updateOptions;
  }

  private get _remainingLhsCccOperations(): number {
    return sumArray(this._current.cccs.map((ccc) => ccc.operations));
  }

  constructor(value: ITransitionElement, cccs: KeyMap<CarbonCausalChain>, cccModelUtility: CCCModelUtility) {
    this._id = <TransitionElement>value.transitionElement;
    this._transitionType = <TransitionElementType>value.type;
    this._subsector = getSubsectorForTransitionElement(value.transitionElement as TransitionElement);
    this._resourceOptions =
      value.type === TransitionElementType.ResourceShift
        ? (value as ResourceShiftElement).props.resourcesToUpdate
        : undefined;
    if (value.type === TransitionElementType.Update || value.type === TransitionElementType.Upshift) {
      this._updateOptions = (value as UpdateElement).props.factorsToUpdate.map((f) => ({
        update: f.update,
        type: f.type,
        by: instantiateFactor(f.by),
      }));
    } else if (value.type === TransitionElementType.SupplyUpdate) {
      this._supplyUpdateVariable = (value as SupplyUpdateElement).props.variableToUpdate;
    }
    const mechanisms: { currentMechanism: EvaluatedMechanism; alternativeMechanism: EvaluatedMechanism } =
      this._instantiateMechanisms(value, cccs);
    this._current = mechanisms.currentMechanism;
    this._alternative = mechanisms.alternativeMechanism;

    this._cohort = instantiateFactor(value.cohort);
    if (
      (value.type === TransitionElementType.Update || value.type === TransitionElementType.Upshift) &&
      (value.props as UpshiftUpdateProps).weightInversionExpression
    ) {
      //We can get empty string as weightInversionExpression
      this._weightInversion = instantiateFactor({
        expression: (value.props as UpshiftUpdateProps).weightInversionExpression,
      });
    }
    this._economicStockImpact = cccModelUtility.getEconomicStockImpact(this._id);
    this._primaryUpdateParameterKey = cccModelUtility.getPrimaryUpdatedVariable(this._id);
  }

  calculateCosts(
    tSetup: number,
    tReport: number,
    variables: KeyMap<Variable>,
    resources: KeyMap<Resource>,
    stocks: KeyMap<Stock>,
    globalExternalities: KeyMap<IGlobalExternality>,
    discountRate: number
  ): TransitionElementCosts[] {
    const costs: TransitionElementCosts[] = [];
    const cohortFactor: number = this.getValueFromCohort(variables);
    this._current.cccs?.forEach((ccc) => {
      const cccCosts = ccc.calculateCosts(
        tSetup,
        tReport,
        variables,
        resources,
        stocks,
        globalExternalities,
        cohortFactor,
        discountRate,
        this._id,
        this._economicStockImpact
      );
      costs.push({ cccId: ccc.id, costs: cccCosts });
    });
    this._alternative.cccs?.forEach((ccc) => {
      const cccCosts = ccc.calculateCosts(
        tSetup,
        tReport,
        variables,
        resources,
        stocks,
        globalExternalities,
        cohortFactor,
        discountRate,
        this._id
      );
      costs.push({ cccId: ccc.id, costs: cccCosts });
    });
    return costs;
  }

  compute(tSetup: number, tReport: number, weight: TargetWeight, variables: KeyMap<Variable>): TransitionData {
    const cohortFactor: number = this.getValueFromCohort(variables);
    const atocLhs = getValueFromFactor(this._current.atoc, variables, { bau: true });

    if (this._transitionType === TransitionElementType.Update) {
      this._eventuallyCalculateWeightFromUiWeight(weight, cohortFactor, variables, tSetup);
      return this._updateTransitionTarget(tSetup, cohortFactor, weight.value, variables);
    } else if (this._transitionType === TransitionElementType.Shift) {
      return this._shiftTransitionTarget(tSetup, tReport, weight.value, atocLhs, cohortFactor, variables);
    } else if (this._transitionType === TransitionElementType.Upshift) {
      this._eventuallyCalculateWeightFromUiWeight(weight, cohortFactor, variables, tSetup);
      return this._upshiftTransitionTarget(tSetup, weight.value, atocLhs, cohortFactor, variables);
    } else if (this._transitionType === TransitionElementType.ResourceShift) {
      return this._shiftResources(tSetup, weight.value, variables);
    }
  }

  computeAfterFinish(
    weight: number,
    variables: KeyMap<Variable>
  ): { transitionedEmissions: number; grossCarbonAbatement: number; operationsAffected: KeyMap<number> } {
    const emissionsFromCccLowEmissionResources = () =>
      sumArray(
        this._current.cccs.map((ccc) => {
          const resourceKey = ccc.getResourceKeyFromProportionParameter(this._resourceOptions.to);
          return ccc.calculateEmissionsPerResourceFromOperations(ccc.operations, variables)[resourceKey];
        })
      );
    const cohortFactor: number = this.getValueFromCohort(variables);
    const emissionsPreResourceShiftPerCcc = this._emissionsFromCurrentOperations(cohortFactor, variables);
    const totalEmissionsPreResourceShift = sumArray(emissionsPreResourceShiftPerCcc);
    const goodEmissionsPreResourceUpdate = emissionsFromCccLowEmissionResources();
    this._applyResourceShift(variables, weight);
    const emissionsPostResourceShiftPerCcc = this._emissionsFromCurrentOperations(cohortFactor, variables);
    const totalEmissionsPostResourceShift = sumArray(emissionsPostResourceShiftPerCcc);
    const goodEmissionsPostResourceUpdate = emissionsFromCccLowEmissionResources();

    this._current.cccs.forEach((ccc, index) => {
      ccc.registerCarbonAbatement(
        this._id,
        emissionsPreResourceShiftPerCcc[index] - emissionsPostResourceShiftPerCcc[index]
      );
    });
    const grossCarbonAbatement = totalEmissionsPreResourceShift - totalEmissionsPostResourceShift;
    const transitionedEmissions = goodEmissionsPostResourceUpdate - goodEmissionsPreResourceUpdate;
    const operationsAffected: KeyMap<number> = this._current.cccs.reduce((acc, ccc) => {
      acc[ccc.id] = ccc.operations;
      return acc;
    }, {});
    return { grossCarbonAbatement, transitionedEmissions, operationsAffected };
  }

  computeUpdateAfterFinish(
    weight: number,
    variables: KeyMap<Variable>
  ): {
    grossCarbonAbatement: number;
    currentParameterValue: number;
    parameterPotential: number;
    unaffectedParameterValue: number;
    capParameterValue: number;
    carbonAbatementPerCcc: KeyMap<number>;
    operationsFullyAffected: KeyMap<number>;
    referenceEnergyIntensity: number;
    referenceEnergyIntensityPerCcc: KeyMap<KeyMap<number>>;
    maxUpdatedEnergyIntensity: number;
    weightedEmissionFactor: number;
  } {
    const cohortFactor: number = this.getValueFromCohort(variables);
    const primaryUpdateParameter = variables[this._primaryUpdateParameterKey];

    // invert the updated parameter(s) with the current weight, getting its state at weight 0
    this._applyParameterUpdate(variables, weight, true); // inverting

    let referenceEnergyIntensity = 0;
    let referenceEnergyIntensityPerCcc: KeyMap<KeyMap<number>> = {};
    let maxUpdatedEnergyIntensity = 0;
    if (this.transitionType === TransitionElementType.Update) {
      ({ energyIntensityPerCcc: referenceEnergyIntensityPerCcc, weightedEnergyIntensity: referenceEnergyIntensity } =
        this._getEnergyIntensity(variables));
    }

    const emissionsBeforeParamApplication = this._emissionsFromCurrentOperations(cohortFactor, variables);
    const totalEmissionsBeforeParamApplication = sumArray(emissionsBeforeParamApplication);
    const unaffectedParameterValue = primaryUpdateParameter?.value;

    // maxing the updated parameter(s) with the weight 1, getting its MAX state
    this._applyParameterUpdate(variables, 1);
    const capParameterValue = primaryUpdateParameter?.value;

    if (this.transitionType === TransitionElementType.Update) {
      ({ weightedEnergyIntensity: maxUpdatedEnergyIntensity } = this._getEnergyIntensity(variables));
    }

    // inverting the changes, and applying the actual weight again, getting the same state as before this function call
    this._applyParameterUpdate(variables, 1, true); // inverting
    this._applyParameterUpdate(variables, weight);

    const currentParameterValue = primaryUpdateParameter?.value;
    const transitionDirection =
      capParameterValue > unaffectedParameterValue ? TransitionUpdateDirection.Up : TransitionUpdateDirection.Down;
    // parameterPotential represents the change already made when Transition Direction Down, otherwise the actual potential
    let parameterPotential;
    if (transitionDirection === TransitionUpdateDirection.Down) {
      parameterPotential = Math.max(unaffectedParameterValue - currentParameterValue, 0);
    } else {
      parameterPotential = Math.max(capParameterValue - currentParameterValue, 0);
    }
    const emissionsAfterParamApplication = this._emissionsFromCurrentOperations(cohortFactor, variables);
    const totalEmissionsAfterParamApplication = sumArray(emissionsAfterParamApplication);
    let grossCarbonAbatement = totalEmissionsBeforeParamApplication - totalEmissionsAfterParamApplication;
    // an abatement less than 1 is neglectable
    grossCarbonAbatement = Math.abs(grossCarbonAbatement) > 1 ? grossCarbonAbatement : 0;
    const carbonAbatementPerCcc: KeyMap<number> = {};
    this._current.cccs.forEach((ccc, index) => {
      carbonAbatementPerCcc[ccc.id] = emissionsBeforeParamApplication[index] - emissionsAfterParamApplication[index];
    });
    const currentOperations: KeyMap<number> = this._currentOperations(cohortFactor);
    const operationsFullyAffected: KeyMap<number> = {};
    Object.keys(currentOperations).forEach((ccc) => {
      operationsFullyAffected[ccc] = currentOperations[ccc] * weight;
    });

    const totalWork = sumArray(this._current.cccs.map((ccc) => sumArray(Object.values(ccc.getWork(variables))))); // assuming all work is in the same unit
    const weightedEmissionFactor = totalWork > 0 ? totalEmissionsAfterParamApplication / totalWork : 0;

    return {
      grossCarbonAbatement,
      currentParameterValue,
      parameterPotential,
      unaffectedParameterValue,
      capParameterValue,
      carbonAbatementPerCcc,
      operationsFullyAffected,
      referenceEnergyIntensity,
      referenceEnergyIntensityPerCcc,
      maxUpdatedEnergyIntensity,
      weightedEmissionFactor,
    };
  }

  computeSupply(t: number, weight: TargetWeight, variables: KeyMap<Variable>): SupplyTransitionData {
    if (this._transitionType === TransitionElementType.SupplyAlteration) {
      return this._supplyAlteration(t, weight.value, variables);
    } else if (this._transitionType === TransitionElementType.SupplyUpdate) {
      return this._supplyUpdate(weight.value, variables);
    }
  }

  invertResourceShifts(weight: number, variables: KeyMap<Variable>, resources: KeyMap<Resource>): void {
    this._applyResourceShift(variables, weight, true);
  }

  invertUpdateChange(weight: number, variables: KeyMap<Variable>): void {
    this._applyParameterUpdate(variables, weight, true);
  }

  getPotential(
    t: number,
    weight: number,
    variables: KeyMap<Variable>
  ): { potential: number; unconstrainedPotential?: number } {
    const cohortFactor: number = this.getValueFromCohort(variables);
    const atocLhsBau = getValueFromFactor(this._current.atoc, variables, { bau: true });
    const atocLhsNew = getValueFromFactor(this._current.atoc, variables, { bau: false });
    const atocRhsNew = getValueFromFactor(this._alternative.atoc, variables, { bau: false });
    let potential = 0;
    let unconstrainedPotential = 0;

    if (this._transitionType === TransitionElementType.Update) {
      const { emission, paramFullyImprovedEmission } = this._evaluatePrePostParamUpdate(
        cohortFactor,
        variables,
        weight
      );
      return { potential: sumArray(Object.values(emission)) - sumArray(Object.values(paramFullyImprovedEmission)) };
    } else if (this._transitionType === TransitionElementType.Shift) {
      const maxOperationShift = sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)));
      const alreadyShiftedOperations = this._activityShift * atocLhsBau;
      const totalOperationsRemaining = this._remainingLhsCccOperations;

      if (totalOperationsRemaining > 0) {
        ({ potential, unconstrainedPotential } = this._getConstrainedAndUnconstrainedPotential(
          maxOperationShift,
          alreadyShiftedOperations,
          totalOperationsRemaining,
          atocLhsBau,
          atocLhsNew,
          atocRhsNew,
          variables
        ));
      }
    } else if (this._transitionType === TransitionElementType.Upshift) {
      const atocCurrent = atocLhsNew;
      const atocBau = getValueFromFactor(this._current.atoc, variables, { bau: true });
      const atocMax = this._getAtocRhsFullyUpdated(variables, weight);
      const cohortOperationsBau = sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)));
      const originalActivityLhs = cohortOperationsBau / atocBau;
      const currentCohortOperations = originalActivityLhs * atocCurrent;
      const expectedRhsOperations = originalActivityLhs * atocMax;
      const maxOperationShift = currentCohortOperations - expectedRhsOperations;

      const totalOperationsRemaining = this._remainingLhsCccOperations;
      if (totalOperationsRemaining > 0) {
        ({ potential, unconstrainedPotential } = this._getConstrainedAndUnconstrainedPotential(
          maxOperationShift,
          0,
          totalOperationsRemaining,
          atocBau,
          atocCurrent,
          atocRhsNew,
          variables,
          TransitionElementType.Upshift
        ));
      }
    } else if (this._transitionType === TransitionElementType.ResourceShift) {
      const { emission, resourceFullyShiftedEmission } = this._evaluatePrePostResourceUpdate(
        cohortFactor,
        variables,
        weight
      );
      return { potential: emission - resourceFullyShiftedEmission };
    }

    return { potential, unconstrainedPotential };
  }

  getValueFromCohort(variables: KeyMap<Variable>): number {
    return getValueFromFactor(this._cohort, variables, {
      bau: false,
      atocLhs: this._current.atoc,
    });
  }

  /*
  One of the responsibilities of this method is to invert a previously applied parameter update.
  It solves this inversion mathematically, and will fail if the updated value has become 0.
  In practice, this situation can only arise if the parameter update is of type savings.
  Until another strategy to obtain the inverted parameter value has been implemented,
  we must not allow parameter updates to alter parameter values in such a way that the value becomes 0.
  The mechanism we use to prevent this is to set a value restriction <100% for max value on savings factor parameters.
   */
  private _applyParameterUpdate(variables: KeyMap<Variable>, weight: number, invert?: boolean): void {
    this._updateOptions.forEach((option) => {
      const currentValue = variables[option.update].value;
      let newValue = currentValue;
      if (option.by) {
        const changeValue = getValueFromFactor(<EvaluatedFactor>option.by, variables);
        const changeFactor = weight * changeValue;
        if (option.type === UpdateType.EfficiencyImprovement) {
          newValue = invert ? currentValue * (1 + changeFactor) : currentValue / (1 + changeFactor);
        } else if (option.type === UpdateType.Savings) {
          newValue = invert ? currentValue / (1 - changeFactor) : currentValue * (1 - changeFactor);
        } else if (option.type === UpdateType.Increase) {
          newValue = invert ? currentValue / (1 + changeFactor) : currentValue * (1 + changeFactor);
        }
      }
      const diff = newValue - currentValue;
      variables[option.update].alterValue(diff);
    });
  }

  private _applyResourceShift(
    variables: KeyMap<Variable>,
    weight: number,
    invert?: boolean
  ): { start: number; shift: number; cap: number; current: number } {
    let result: any = {};
    this._current.cccs.forEach((ccc) => {
      const moveFrom = {
        proportion: this._resourceOptions.from,
        cap: undefined, // Not currently supplied by model anyway
      };
      const moveTo = {
        proportion: this._resourceOptions.to,
        cap: undefined, // Not currently supplied by model anyway
      };
      const resourceCap = variables[moveTo.cap]?.value ?? this._MAX_RESOURCE_PROPORTION_CAP;
      const lowEmissionResourceProportion = variables[moveTo.proportion].value;
      const lowEmissionResourceProportionPreAlteration = variables[moveTo.proportion].value;
      const proportionToAlter = invert
        ? lowEmissionResourceProportion - variables[moveTo.proportion].referenceValue
        : (resourceCap - lowEmissionResourceProportion) * weight;
      variables[moveFrom.proportion].alterValue(invert ? proportionToAlter : -proportionToAlter);
      variables[moveTo.proportion].alterValue(invert ? -proportionToAlter : proportionToAlter);

      result.current = variables[moveFrom.proportion].value;
      result.shift = variables[moveTo.proportion].value;
      result.start = lowEmissionResourceProportionPreAlteration;
      result.cap = resourceCap;
    });
    return result;
  }

  private _cloneVariables(variables: KeyMap<Variable>): KeyMap<Variable> {
    //Using Object.keys instead of Object.entries because it is faster and this is called a lot during a full timeline run of ClimateCortex.
    return Object.keys(variables).reduce((acc, key) => {
      acc[key] = variables[key].clone();
      return acc;
    }, {});
  }

  private _currentOperations(cohortFactor: number): KeyMap<number> {
    return this._current.cccs.reduce((acc, ccc) => {
      acc[ccc.id] = ccc.operations * cohortFactor;
      return acc;
    }, {});
  }

  private _emissionsFromCurrentOperations(cohortFactor: number, variables: KeyMap<Variable>): number[] {
    return this._current.cccs.map(
      (ccc) => cohortFactor * ccc.calculateEmissionsFromOperations(ccc.operations, variables)
    );
  }

  private _evaluatePrePostParamUpdate(
    cohortFactor: number,
    variables: KeyMap<Variable>,
    weight?: number
  ): { emission: KeyMap<number>; emissionBau: KeyMap<number>; paramFullyImprovedEmission: KeyMap<number> } {
    const emission = this._getEmissionsFromCurrentOperations(cohortFactor, variables);
    const fakeVariables = this._cloneVariables(variables);
    if (weight !== undefined) {
      this._applyParameterUpdate(fakeVariables, weight, true); // invert the change already done to variable
    }
    const emissionBau = this._getEmissionsFromCurrentOperations(cohortFactor, fakeVariables);
    this._applyParameterUpdate(fakeVariables, 1); // maximize a potential change with weight 1
    const paramFullyImprovedEmission = this._getEmissionsFromCurrentOperations(cohortFactor, fakeVariables);
    return { emission, emissionBau, paramFullyImprovedEmission };
  }

  private _evaluatePrePostResourceUpdate(
    cohortFactor: number,
    variables: KeyMap<Variable>,
    weight?: number
  ): { emission: number; emissionBau: number; resourceFullyShiftedEmission: number } {
    const emission = this._sumEmissionsFromCurrentOperations(cohortFactor, variables);
    const fakeVariables = this._cloneVariables(variables);
    if (weight !== undefined) {
      this._applyResourceShift(fakeVariables, weight, true); // invert the change already done to variable
    }
    const emissionBau = this._sumEmissionsFromCurrentOperations(cohortFactor, fakeVariables);
    this._applyResourceShift(fakeVariables, 1); // maximize a potential change with weight 1
    const resourceFullyShiftedEmission = this._sumEmissionsFromCurrentOperations(cohortFactor, fakeVariables);
    return { emission, emissionBau, resourceFullyShiftedEmission };
  }

  private _eventuallyCalculateWeightFromUiWeight(
    weight: TargetWeight,
    cohortFactor: number,
    variables: KeyMap<Variable>,
    t: number
  ): void {
    if (weight.isUI && this._weightInversion) {
      const atocLhsBau = getValueFromFactor(this._current.atoc, variables, { bau: true });
      const operationsBau = sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)));
      const improvement = getValueFromFactor(<EvaluatedFactor>this._updateOptions[0].by, variables); // take a sample from updateOptions
      let newWeight = weight.value;
      if (this._transitionType === TransitionElementType.Upshift) {
        const subFraction = weight.value * (1 - 1 / (1 + improvement));
        const operationsShifted = subFraction * operationsBau;
        const unknownWeightAtocX = atocLhsBau * (1 - operationsShifted / operationsBau);
        newWeight = getValueFromFactor(this._weightInversion, variables, {
          bau: false,
          unknownX: unknownWeightAtocX,
          atocLhs: this._current.atoc,
          atocRhs: this._alternative.atoc,
          t,
        });
      } else if (this._transitionType === TransitionElementType.Update) {
        const { emissionBau, paramFullyImprovedEmission } = this._evaluatePrePostParamUpdate(cohortFactor, variables);
        const variablesInWeightInversionFn = this._weightInversion.expressionVariables;
        const cccSample = this._current.cccs.find((ccc) =>
          ccc.work.find((work) => {
            const variableSample =
              (<EvaluatedFactor>work.operationToWork).expressionVariables?.length > 0
                ? (<EvaluatedFactor>work.operationToWork).expressionVariables[0]
                : undefined;
            return variablesInWeightInversionFn.includes(variableSample);
          })
        );
        if (cccSample) {
          const cccSampleWantedSaveOfEmissions =
            weight.value * (emissionBau[cccSample.id] - paramFullyImprovedEmission[cccSample.id]);
          const cccSampleEmissionBau = emissionBau[cccSample.id];

          /**
           * wantedSaveofEmission = cccSampleEmissionBau - ops * unknown_efficiency * emission_factor
           * cccSampleemissionBau - wantedSaveofEmission = ops * unknown_efficiency * emission_factor
           * ( cccSampleemissionBau - wantedSaveofEmission ) / (ops * emission_factor )  = unknown_efficiency
           */
          const unknownWeightEfficiencyX =
            (cccSampleEmissionBau - cccSampleWantedSaveOfEmissions) /
            (cccSample.operations * cccSample.getSimpleCccEmissionFactorForWeightInversion(variables));
          newWeight = getValueFromFactor(this._weightInversion, variables, {
            bau: false,
            unknownX: unknownWeightEfficiencyX,
            atocLhs: this._current.atoc,
            atocRhs: this._alternative.atoc,
            t,
          });
        }
      }

      weight.value = Math.max(Math.min(newWeight, 1), 0);
      delete weight.isUI;
    }
  }

  private _getAlternativeEmissionBau(cohortFactor: number, t: number, variables: KeyMap<Variable>): number {
    return sumArray(this._alternative.cccs.map((ccc) => cohortFactor * ccc.getEmissionsBau(t, variables)));
  }

  private _getAtocRhsFullyUpdated(variables: KeyMap<Variable>, weight: number): number {
    const fakeVariables = this._cloneVariables(variables);
    this._applyParameterUpdate(fakeVariables, weight, true); // invert the change already done to variable
    this._applyParameterUpdate(fakeVariables, 1); // maximize a potential change with weight 1
    return getValueFromFactor(this._alternative.atoc, fakeVariables, { bau: false });
  }

  private _getConstrainedAndUnconstrainedPotential(
    maxOperationShift: number,
    alreadyShiftedOperations: number,
    totalOperationsRemaining: number,
    atocLhsBau: number,
    atocLhsCurrent: number,
    atocRhsCurrent: number,
    variables: KeyMap<Variable>,
    transitionType?: TransitionElementType
  ): { potential: number; unconstrainedPotential: number } {
    const shiftableOperations = Math.min(
      Math.max(maxOperationShift - alreadyShiftedOperations, 0),
      totalOperationsRemaining
    );
    const maximumTransitionedOperations = (shiftableOperations / atocLhsCurrent) * atocRhsCurrent;
    const maximumTransitionedOperationsBau = (shiftableOperations / atocLhsBau) * atocRhsCurrent;
    const remainingOperationsProportionPerCcc = this._getRemainingOperationsProportionPerCcc(totalOperationsRemaining);
    // potential is based on net variables, and net ccc
    let potential = this._getPotential(
      shiftableOperations,
      remainingOperationsProportionPerCcc,
      maximumTransitionedOperations,
      variables,
      { netUtilisationParameters: true, netCccs: true }
    );
    if (transitionType !== TransitionElementType.Upshift) {
      potential *= atocLhsCurrent / atocLhsBau;
    }
    // unconstrained potential is based on gross variables, and net ccc
    const unconstrainedPotential = this._getPotential(
      shiftableOperations,
      remainingOperationsProportionPerCcc,
      maximumTransitionedOperationsBau,
      variables,
      { netUtilisationParameters: false, netCccs: true }
    );
    return { potential, unconstrainedPotential };
  }

  private _getEmissionBau(cohortFactor: number, t: number, variables: KeyMap<Variable>): number {
    return sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getEmissionsBau(t, variables)));
  }

  private _getEmissionsFromCurrentOperations(cohortFactor: number, variables: KeyMap<Variable>): KeyMap<number> {
    const emissions: KeyMap<number> = {};
    this._current.cccs.forEach((ccc) => {
      emissions[ccc.id] = cohortFactor * ccc.calculateEmissionsFromOperations(ccc.operations, variables);
    });
    return emissions;
  }

  private _getEnergyIntensity(variables: KeyMap<Variable>): {
    energyIntensityPerCcc: KeyMap<KeyMap<number>>;
    weightedEnergyIntensity: number;
  } {
    const energyIntensityPerCcc: KeyMap<KeyMap<number>> = this._current.cccs.reduce((acc, ccc) => {
      acc[ccc.id] = ccc.work.reduce((acc, work) => {
        acc[work.work] = getValueFromFactor(<EvaluatedFactor>work.operationToWork, variables);
        return acc;
      }, {});
      return acc;
    }, {});
    const operations: KeyMap<number> = this._current.cccs.reduce((acc, ccc) => {
      acc[ccc.id] = ccc.operations;
      return acc;
    }, {});
    const totalOperations = sumArray(Object.values(operations)); // assuming all cccs have the same operations unit
    let weightedEnergyIntensity = 0;
    if (totalOperations > 0) {
      this._current.cccs.forEach((ccc) => {
        const energyIntensity: number = Object.values(energyIntensityPerCcc[ccc.id])[0] ?? 0; // assuming only one work per ccc
        weightedEnergyIntensity += (operations[ccc.id] / totalOperations) * energyIntensity;
      });
    }
    return { energyIntensityPerCcc, weightedEnergyIntensity };
  }

  private _getPotential(
    shiftableOperations: number,
    remainingOperationsProportionPerCcc: number[],
    maximumTransitionedOperations: number,
    variables: KeyMap<Variable>,
    options: { netUtilisationParameters: boolean; netCccs: boolean } = {
      netUtilisationParameters: false,
      netCccs: false,
    }
  ): number {
    let potential = sumArray(
      this._current.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(
          shiftableOperations * remainingOperationsProportionPerCcc[index],
          variables,
          !options.netUtilisationParameters
        )
      )
    );
    if (options.netCccs && this._alternative.cccs) {
      const alternativeProportionPerCcc = this._alternative.cccs.length > 1 ? remainingOperationsProportionPerCcc : [1];
      const netMaximumTransitionedEmissions = sumArray(
        this._alternative.cccs.map((ccc, index) =>
          ccc.calculateEmissionsFromOperations(
            maximumTransitionedOperations * alternativeProportionPerCcc[index],
            variables,
            !options.netUtilisationParameters
          )
        )
      );
      potential -= netMaximumTransitionedEmissions;
    }
    return Math.max(potential, 0);
  }

  private _getRemainingOperationsProportionPerCcc(totalOperationsRemaining: number): number[] {
    // safe guard if function is accidentally called when totalOperationsRemaining is 0
    return this._current.cccs.map((ccc) => ccc.operations / totalOperationsRemaining || 0);
  }

  private _instantiateMechanism(atoc: string, cccIds: string[], cccs: KeyMap<CarbonCausalChain>): EvaluatedMechanism {
    let evaluatedAtoc: EvaluatedFactor;
    let cccsInMechanism: CarbonCausalChain[] = [];
    if (atoc !== undefined) {
      evaluatedAtoc = instantiateFactor({ expression: atoc });
    }
    if (cccIds !== undefined) {
      cccsInMechanism = cccIds.map((cccId) => cccs[cccId]);
    }

    return {
      atoc: evaluatedAtoc,
      cccs: cccsInMechanism,
      cccIds,
    };
  }

  private _instantiateMechanisms(
    transitionElement: ITransitionElement,
    cccs: KeyMap<CarbonCausalChain>
  ): { currentMechanism: EvaluatedMechanism; alternativeMechanism: EvaluatedMechanism } {
    let current: EvaluatedMechanism;
    let alternative: EvaluatedMechanism;
    if (transitionElement.type === TransitionElementType.Shift) {
      const shift: ShiftElement = <ShiftElement>transitionElement;
      current = this._instantiateMechanism(shift.props.shiftFrom.atoc, shift.props.shiftFrom.chains, cccs);
      alternative = this._instantiateMechanism(shift.props.shiftTo.atoc, shift.props.shiftTo.chains, cccs);
    } else {
      const updateCarbonCausalChainOptions: UpdateCarbonCausalChainOptions = (
        transitionElement.props as TransitionElementProps
      ).carbonCausalChains;
      current = this._instantiateMechanism(
        updateCarbonCausalChainOptions.atoc,
        updateCarbonCausalChainOptions.chains,
        cccs
      );
      alternative = this._instantiateMechanism(updateCarbonCausalChainOptions.atoc, [], cccs);
    }
    return { currentMechanism: current, alternativeMechanism: alternative };
  }

  private _performActivityShift(
    tReport: number,
    plannedOperationShift: number,
    weight: number
  ): { newWeight: number | undefined; operationShiftPerCcc: number[]; remainingOperationsProportionPerCcc: number[] } {
    let newWeight: number;
    let operationShiftPerCcc: number[];
    const totalOperationsRemaining = this._remainingLhsCccOperations;
    const remainingOperationsProportionPerCcc: number[] =
      this._getRemainingOperationsProportionPerCcc(totalOperationsRemaining);
    if (totalOperationsRemaining > 0) {
      operationShiftPerCcc = this._current.cccs.map((ccc, index) => {
        const operationShift = plannedOperationShift * remainingOperationsProportionPerCcc[index];
        if (operationShift > 0) {
          const successfullyShiftedOperations = ccc.alterOperation(-operationShift, tReport, this._id);
          const operationShiftedFraction = Math.abs(successfullyShiftedOperations) / operationShift;
          if (!isNaN(operationShiftedFraction) && operationShiftedFraction !== 1) {
            newWeight = operationShiftedFraction * weight;
          }
          return -successfullyShiftedOperations;
        }
        return 0;
      });
    } else {
      newWeight = 0;
      operationShiftPerCcc = this._current.cccs.map((_) => 0);
    }
    return { newWeight, operationShiftPerCcc, remainingOperationsProportionPerCcc };
  }

  private _shiftResources(t: number, weight: number, variables: KeyMap<Variable>): TransitionResourceShiftData {
    const emissionBau: number = sumArray(
      this._current.cccs.map((ccc) => ccc.calculateEmissionsFromOperations(ccc.operations, variables, true))
    );
    const resourceShiftResult = this._applyResourceShift(variables, weight);
    return {
      emissionBau,
      activityShift: resourceShiftResult.shift,
      proportionCap: resourceShiftResult.cap,
      proportionStart: resourceShiftResult.start,
      currentActivities: resourceShiftResult.current,
      carbonAbatement: { gross: 0 },
      transitionedEmissions: 0,
      requestedWeight: weight,
      operationsAffected: {},
      activityToCarbonAbatementFactor: undefined,
    };
  }

  private _shiftTransitionTarget(
    tSetup: number,
    tReport: number,
    weight: number,
    atocLhs: number,
    cohortFactor: number,
    variables: KeyMap<Variable>
  ): TransitionShiftData | undefined {
    if (
      this._transitionType !== TransitionElementType.Shift ||
      this._current.cccs === undefined ||
      this._current.cccs.length === 0 ||
      this._alternative.cccs === undefined ||
      this._alternative.cccs.length === 0
    ) {
      return undefined;
    }
    const atocRhs = getValueFromFactor(this._alternative.atoc, variables, { bau: true });
    // Emissions business-as-usual
    const emissionBau: number = this._getEmissionBau(cohortFactor, tSetup, variables);

    // Activities Shifted
    const plannedOperationShift = sumArray(
      this._current.cccs.map((ccc) => weight * cohortFactor * ccc.getOperationsBau(tSetup))
    );
    const maxAlternativeOperationShift =
      (sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(tSetup))) / atocLhs) * atocRhs;
    const { newWeight, operationShiftPerCcc } = this._performActivityShift(tReport, plannedOperationShift, weight);
    const activityShiftPerCcc = operationShiftPerCcc.map((operationShift) => operationShift / atocLhs);
    this._activityShift = sumArray(activityShiftPerCcc);

    const currentActivitiesPerCcc: number[] = this._current.cccs.map(
      (ccc) => ((1 - (newWeight ?? weight)) * cohortFactor * ccc.getOperationsBau(tSetup)) / atocLhs
    );
    const bauActivitiesPerCcc = this._current.cccs.map(
      (ccc) => (cohortFactor * ccc.getOperationsBau(tSetup)) / atocLhs
    );

    // Add operation to low-carbon carbon causal chain(s)
    const activityShiftPerAlternativeCcc =
      this._alternative.cccs.length > 1 ? activityShiftPerCcc : [this._activityShift];
    this._alternative.cccs.forEach((ccc, index) =>
      ccc.alterOperation(activityShiftPerAlternativeCcc[index] * atocRhs, tReport, this._id)
    );
    const alternativeActivityShiftProportionPerCcc = activityShiftPerAlternativeCcc.map(
      (activity) => activity / this._activityShift || 0
    );

    // Transitioned Emissions Bau
    const transitionedEmissionsBau = sumArray(
      this._alternative.cccs.map((ccc, index) => {
        const transitionedEmissions = ccc.calculateEmissionsFromOperations(
          activityShiftPerAlternativeCcc[index] * atocRhs,
          variables,
          true
        );
        ccc.registerCarbonAbatement(this._id, -transitionedEmissions);
        return transitionedEmissions;
      })
    );
    // Transitioned Emissions
    const transitionedEmissions = sumArray(
      this._alternative.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(activityShiftPerAlternativeCcc[index] * atocRhs, variables)
      )
    );

    // Remaining emissions from Lhs cccs
    const emission = sumArray(
      this._current.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(currentActivitiesPerCcc[index] * atocLhs, variables)
      )
    );
    // Lhs cccs bau emissions based on remaining operations
    const remainingOperationsBauEmission = sumArray(
      this._current.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(currentActivitiesPerCcc[index] * atocLhs, variables, true)
      )
    );

    const activitiesBau = sumArray(bauActivitiesPerCcc);
    const currentActivities = sumArray(currentActivitiesPerCcc);
    const net = emissionBau - (remainingOperationsBauEmission + transitionedEmissionsBau);
    // an abatement less than 1 is neglectable
    const netCarbonAbatement = Math.abs(net) > 1 ? net : 0;

    // gross carbon abatement (saved)
    const operationsShiftedPerCcc: number[] = this._current.cccs.map(
      (ccc) => (newWeight ?? weight) * cohortFactor * ccc.getOperationsBau(tSetup)
    );
    const grossCarbonAbatement = sumArray(
      this._current.cccs.map((ccc, index) => {
        const carbonAbatement = ccc.calculateEmissionsFromOperations(operationsShiftedPerCcc[index], variables, true);
        ccc.registerCarbonAbatement(this._id, carbonAbatement);
        return carbonAbatement;
      })
    );

    // maximum potential transitioned emissions, net(variables)
    const maxTransitionedEmissions = sumArray(
      this._alternative.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(
          maxAlternativeOperationShift * alternativeActivityShiftProportionPerCcc[index],
          variables,
          true
        )
      )
    );
    const maximumNetLhsCccSavings = emissionBau - maxTransitionedEmissions;

    const alternativeActivitiesBau = sumArray(
      this._alternative.cccs.map((ccc) => ccc.getOperationsBau(tSetup) / atocRhs)
    );
    const alternativeEmissionBau = this._getAlternativeEmissionBau(cohortFactor, tSetup, variables);

    const operationsShifted: KeyMap<number> = {};
    operationShiftPerCcc.forEach((operationShift, index) => {
      operationsShifted[this._current.cccs![index].id] = -operationShift;
    });
    this._alternative.cccs.forEach((ccc, index) => {
      operationsShifted[ccc.id] = activityShiftPerAlternativeCcc[index] * atocRhs;
    });

    const transitionInformation: TransitionData = {
      activityShift: this._activityShift,
      carbonAbatement: {
        gross: grossCarbonAbatement,
        net: netCarbonAbatement,
      },
      maximumNetLhsCccSavings,
      currentActivities,
      emission,
      emissionBau,
      transitionedEmissions,
      activitiesBau,
      alternativeActivitiesBau,
      alternativeEmissionBau,
      operationsShifted,
      requestedWeight: weight,
      activityToCarbonAbatementFactor: undefined,
    };
    if (!isNaN(newWeight)) {
      transitionInformation.newWeight = newWeight;
    }
    return transitionInformation;
  }

  private _sumEmissionsFromCurrentOperations(cohortFactor: number, variables: KeyMap<Variable>): number {
    return sumArray(this._emissionsFromCurrentOperations(cohortFactor, variables));
  }

  private _supplyAlteration(tReport: number, weight: number, variables: KeyMap<Variable>): SupplyAlterationData {
    let totalAlteration = 0;
    let operations = 0;
    let emissionsFromCcc = 0;
    this._current.cccs.forEach((ccc) => {
      // SUPPLY_ALTERATION should only support one CCC
      const primaryOutputToOperationsConversion = ccc.getPOtOC(variables);
      const operationsTarget = primaryOutputToOperationsConversion * weight;
      const alteration = operationsTarget - ccc.operations;
      ccc.alterOperation(alteration, tReport, this._id);
      totalAlteration += alteration;
      operations += ccc.operations / primaryOutputToOperationsConversion;
      emissionsFromCcc += ccc.calculateEmissionsFromOperations(ccc.operations, variables);
    });
    return { totalAlteration, operations, emissionsFromCcc };
  }

  private _supplyUpdate(weight: number, variables: KeyMap<Variable>): SupplyUpdateData {
    const variableName = this._supplyUpdateVariable;
    if (variableName === undefined) {
      throw new Error(`model contains errors, Supply Update "${this._id}" can't find 1 factorToUpdate`);
    }
    const delta = weight - variables[variableName].value;
    variables[variableName].alterValue(delta);
    const value = variables[variableName].value;
    const originalValue = variables[variableName].referenceValue;
    return { value, originalValue, parameterUnit: variables[variableName].unit };
  }

  private _updateTransitionTarget(
    t: number,
    cohortFactor: number,
    weight: number,
    variables: KeyMap<Variable>
  ): TransitionUpdateData {
    // Emissions business-as-usual
    const emissionBau: number = this._getEmissionBau(cohortFactor, t, variables);
    this._applyParameterUpdate(variables, weight);
    const currentOperations: number = sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)));
    const atoc = getValueFromFactor(this._current.atoc, variables, { bau: true });
    const activityShift = (currentOperations * weight) / atoc;
    const currentActivities = (currentOperations * (1 - weight)) / atoc;
    return {
      activityShift,
      carbonAbatement: {
        gross: 0,
      },
      currentActivities,
      emissionBau,
      potential: {
        constrained: 0,
        unconstrained: 0,
      },
      requestedWeight: weight,
      operationsFullyAffected: {},
      referenceEnergyIntensity: 0,
      referenceEnergyIntensityPerCcc: {},
      maxUpdatedEnergyIntensity: 0,
      weightedEmissionFactor: 0,
      activityToCarbonAbatementFactor: undefined,
    };
  }

  private _upshiftTransitionTarget(
    t: number,
    weight: number,
    atocLhs: number,
    cohortFactor: number,
    variables: KeyMap<Variable>
  ): TransitionUpshiftData {
    // Emissions business-as-usual
    const emissionBau: number = this._getEmissionBau(cohortFactor, t, variables);
    this._applyParameterUpdate(variables, weight);
    const atocCurrent = getValueFromFactor(this._alternative.atoc, variables, { bau: false });

    // Eventual activity reduction
    const allOperationsInScope = sumArray(this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)));
    const activityLhsInScope = allOperationsInScope / atocLhs;
    const expectedRhsOperations = activityLhsInScope * atocCurrent;
    const plannedOperationShift = Math.max(allOperationsInScope - expectedRhsOperations, 0);
    const { newWeight, operationShiftPerCcc, remainingOperationsProportionPerCcc } = this._performActivityShift(
      t,
      plannedOperationShift,
      weight
    );

    let workAvoided: number = 0;
    const operationsUpshifted: KeyMap<number> = {};
    const operationsInScope: KeyMap<number> = {};
    this._current.cccs.forEach((ccc, index) => {
      operationsInScope[ccc.id] = cohortFactor * ccc.getOperationsBau(t);
      const operations = operationShiftPerCcc[index];
      operationsUpshifted[ccc.id] = operations;
      workAvoided += ccc.calculateWorkFromOperations(operations, variables);
    });

    const grossCarbonAbatement = sumArray(
      this._current.cccs.map((ccc, index) => {
        const carbonAbatement = ccc.calculateEmissionsFromOperations(operationShiftPerCcc[index], variables, true);
        ccc.registerCarbonAbatement(this._id, carbonAbatement);
        return carbonAbatement;
      })
    );

    let weightedEnergyIntensity = 0;
    const totalShiftedOperations = sumArray(operationShiftPerCcc);

    operationShiftPerCcc.forEach((operationsShiftedOnThisCCC, index) => {
      const weight = totalShiftedOperations !== 0 ? operationsShiftedOnThisCCC / totalShiftedOperations : 0;
      const energyIntensity = this._current.cccs[index].calculateSimpleEnergyIntensityForCcc(variables, true);
      weightedEnergyIntensity += weight * energyIntensity;
    });

    // emissions within reach
    const atocMax = this._getAtocRhsFullyUpdated(variables, weight);
    const atocBau = getValueFromFactor(this._alternative.atoc, variables, { bau: true });
    const operationsInReach = allOperationsInScope * (1 - atocMax / atocBau);
    const emissionsInReachBau = sumArray(
      this._current.cccs.map((ccc, index) =>
        ccc.calculateEmissionsFromOperations(
          remainingOperationsProportionPerCcc[index] * operationsInReach,
          variables,
          true
        )
      )
    );
    const currentOperations: number = sumArray(
      this._current.cccs.map((ccc) => cohortFactor * ccc.getOperationsBau(t)) // Note: should be using updated operations since this is an upshift
    );
    const activityShift = (currentOperations * weight) / atocLhs; // Note: should be using atocCurrent since this is an upshift
    const currentActivities = (currentOperations * (1 - weight)) / atocLhs; // Note: should be using atocCurrent since this is an upshift
    const weightedEmissionFactor = workAvoided > 0 ? grossCarbonAbatement / workAvoided : 0; // this implementation assumes that all CCCs have the same work unit
    const transitionInformation: TransitionUpshiftData = {
      activityShift,
      carbonAbatement: {
        gross: grossCarbonAbatement,
      },
      operationsUpshifted,
      operationsInScope,
      currentActivities,
      emissionBau,
      emissionsInReachBau,
      requestedWeight: weight,
      weightedEnergyIntensity,
      weightedEmissionFactor,
      activityToCarbonAbatementFactor: undefined,
    };
    if (!isNaN(newWeight)) {
      transitionInformation.newWeight = newWeight;
    }
    return transitionInformation;
  }
}
